import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCoaGroups } from 'hooks/api/useAccounts';
import { accountTypeService, accountSubtypeService, coaGroupService } from 'api/services';
import type { CoaGroupDto, CoaGroupQueryParams } from 'api/types';
import { AccountGroupFormModal, AccountGroupViewModal } from '../../../modals/modules/accounts/AccountGroupFormModal';

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'groupCode', label: 'Code' },
  { id: 'groupName', label: 'Group Name' },
  { id: 'subtypeName', label: 'Subtype' },
  { id: 'accountTypeName', label: 'Type' },
  { id: 'parentGroupName', label: 'Parent' },
  { id: 'childCount', label: 'Sub-groups' },
  { id: 'ledgerCount', label: 'Ledgers' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface AccountGroupsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const AccountGroupsScreen: React.FC<AccountGroupsScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('account-groups');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('account-groups-columns', COLUMN_DEFS);

  // Server-side pagination + filters
  const [queryParams, setQueryParams] = useState<CoaGroupQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCoaGroups(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [groupCodeFilter, setGroupCodeFilter] = useState('');
  const [groupNameFilter, setGroupNameFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CoaGroupDto | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  // Slot-reuse Add — finds the lowest unused (N) so closed slots get reused
  const handleAddNew = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelectedRecord(null); setFormModalOpen(true);
  }, [tabId, isMinimized]);

  const handleView = useCallback((id: string) => {
    const record = data.find(g => g.id === id);
    if (!record) return;
    const viewId = tabId ? `${tabId}-view-modal-${record.groupCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setViewModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleEdit = useCallback((id: string) => {
    const record = data.find(g => g.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${record.groupCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const record = data.find(g => g.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-copy-${record.groupCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formModalOpen) formModalMinimizeRef.current?.();
    // Copy uses record-keyed IDs; the counter is retained for legacy prop compatibility.
    setCopyInstanceId(record.groupCode);
    setFormMode('copy'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal, formModalOpen]);

  const handleDelete = useCallback(async (id: string) => {
    const record = data.find(g => g.id === id);
    if (!record) return;
    const ok = await confirmDelete({ resourceLabel: record.groupName });
    if (!ok) return;
    try {
      await coaGroupService.delete(record.id, record.rowVersion);
      notificationService.toast.success(`Group "${record.groupName}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.');
    }
  }, [data, confirmDelete, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false); setSelectedRecord(null); refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, groupCode?: string) => {
      if (instanceId) {
        if (restoredMode === 'copy') setCopyInstanceId(instanceId);
        else setCreateInstanceId(instanceId);
      }
      if (groupCode) {
        const rec = data.find(g => g.groupCode === groupCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode); setFormModalOpen(true);
    },
    [data],
  );

  const handleRestoreView = useCallback((groupCode: string) => {
    const rec = data.find(g => g.groupCode === groupCode);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  const tableColumns: SyutGridColumn<CoaGroupDto>[] = useMemo(() => [
    { id: 'groupCode', header: 'Code', accessorKey: 'groupCode', size: 110 },
    { id: 'groupName', header: 'Group Name', accessorKey: 'groupName', size: 220 },
    { id: 'subtypeName', header: 'Subtype', accessorKey: 'subtypeName', size: 170 },
    { id: 'accountTypeName', header: 'Type', accessorKey: 'accountTypeName', size: 120 },
    {
      id: 'parentGroupName', header: 'Parent', accessorKey: 'parentGroupName',
      size: 160, enableSorting: false, cell: ({ getValue }) => (getValue() as string) || '—',
    },
    { id: 'childCount', header: 'Sub-groups', accessorKey: 'childCount', size: 110 },
    { id: 'ledgerCount', header: 'Ledgers', accessorKey: 'ledgerCount', size: 90 },
    {
      id: 'status', header: 'Status', accessorKey: 'statusName', size: 110,
      cell: ({ row }) => {
        const r = row.original as CoaGroupDto;
        return (
          <span className="badge"
            style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((rec: CoaGroupDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec.id) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rec.id) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rec.id) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(rec.id), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAddNew, 'Add Account Group'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const loadTypeOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await accountTypeService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      return [{ value: '', label: 'All Types' }, ...res.data.map(t => ({ value: t.id, label: t.typeName }))];
    } catch {
      return [{ value: '', label: 'All Types' }];
    }
  }, []);
  const loadSubtypeOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await accountSubtypeService.getAll({
        accountTypeId: typeFilter || undefined, searchTerm: input || undefined, pageSize: 100,
      });
      return [{ value: '', label: 'All Subtypes' }, ...res.data.map(s => ({ value: s.id, label: s.subtypeName }))];
    } catch {
      return [{ value: '', label: 'All Subtypes' }];
    }
  }, [typeFilter]);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      accountTypeId: typeFilter || undefined,
      accountSubtypeId: subtypeFilter || undefined,
      groupCode: groupCodeFilter || undefined,
      groupName: groupNameFilter || undefined,
      includeInactive: includeInactive || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [typeFilter, subtypeFilter, groupCodeFilter, groupNameFilter, includeInactive]);

  const clearFilters = useCallback(() => {
    setTypeFilter(''); setSubtypeFilter(''); setGroupCodeFilter(''); setGroupNameFilter(''); setIncludeInactive(false);
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [typeFilter, subtypeFilter, groupCodeFilter, groupNameFilter, includeInactive ? '1' : '']
    .filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Account Groups"
      description="Organize the chart of accounts into groups under each subtype"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search groups...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type" isAsync loadOptions={loadTypeOptions} defaultOptions cacheOptions={false}
                  value={typeFilter}
                  onChange={(v) => { setTypeFilter((v as string) || ''); setSubtypeFilter(''); }}
                  placeholder="All Types" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect key={`f-sub-${typeFilter}`} label="Subtype" isAsync loadOptions={loadSubtypeOptions}
                  defaultOptions cacheOptions={false} value={subtypeFilter}
                  onChange={(v) => setSubtypeFilter((v as string) || '')}
                  placeholder="All Subtypes" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Group Code</label>
                <input type="text" className="form-input" value={groupCodeFilter}
                  onChange={(e) => setGroupCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Group Name</label>
                <input type="text" className="form-input" value={groupNameFilter}
                  onChange={(e) => setGroupNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No account groups found'}
            frozenLeftColumns={['select', 'groupCode']} frozenRightColumns={['actions']}
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.folder size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No account groups found'}</p>
                </div>
              ) : (
                data.map((g) => (
                  <div key={g.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{g.groupCode}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>{g.groupName}</h3>
                      </div>
                      <ActionMenu items={getRowActions(g)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {g.accountTypeName} · {g.subtypeName}
                    </div>
                    <div style={{
                      display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)',
                      paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)',
                    }}>
                      <span>Sub-groups: {g.childCount}</span>
                      <span>Ledgers: {g.ledgerCount}</span>
                      <span className="badge" style={{ marginLeft: 'auto' }}>{g.statusName || 'Active'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AccountGroupFormModal isOpen={formModalOpen} mode={formMode} group={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        openedBy="AccountGroupsScreen"
        tabId={tabId} moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef} />

      <AccountGroupViewModal isOpen={viewModalOpen} group={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        onEdit={(rec) => handleEdit(rec.id)}
        tabId={tabId} moduleName={moduleName}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default AccountGroupsScreen;
